import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Target } from 'lucide-react';

interface OnboardingModalProps {
  isOpen: boolean;
  onComplete: () => void;
}

const OnboardingModal: React.FC<OnboardingModalProps> = ({ isOpen, onComplete }) => {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleComplete = async () => {
    if (!user || !name.trim()) return;

    setLoading(true);
    try {
      // Update the profiles table with just the name
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          name: name.trim()
        })
        .eq('id', user.id);

      if (profileError) {
        console.error('Error updating profile:', profileError);
        toast.error('Failed to save your preferences');
        return;
      }

      // Also update the user metadata to keep them in sync
      const { error: userError } = await supabase.auth.updateUser({
        data: {
          name: name.trim(),
          display_name: name.trim()
        }
      });

      if (userError) {
        console.error('Error updating user metadata:', userError);
        // Don't show error to user since profile was saved successfully
        console.log('Profile saved but user metadata update failed');
      }

      toast.success('Welcome! Your profile has been set up.');
      onComplete();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-center">
            Welcome! Let's get started
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground">
                Help us personalize your experience
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">
                What should we call you?
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                className="h-11"
                autoFocus
              />
            </div>

            <Button 
              onClick={handleComplete}
              className="w-full h-11 font-medium" 
              disabled={!name.trim() || loading}
            >
              {loading ? 'Setting up...' : 'Complete setup'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingModal;
